import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { siteEntries, latestArticles, faqList, mangaList } from '@/mocks/siteData';

function EntryCard({ entry }: { entry: typeof siteEntries[0] }) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-medium text-foreground-950 whitespace-nowrap">{entry.name}</h3>
        <span
          className={`px-2 py-0.5 rounded-md text-xs font-medium whitespace-nowrap ${
            entry.status === '可用'
              ? 'bg-accent-100 text-accent-700'
              : 'bg-secondary-200 text-secondary-700'
          }`}
        >
          {entry.status}
        </span>
      </div>
      <p className="text-sm text-foreground-600 leading-relaxed flex-1">{entry.description}</p>
      <div className="flex items-center justify-between">
        <span className="text-xs text-foreground-400 flex items-center gap-1">
          <i className="ri-speed-up-line text-sm"></i>
          {entry.speed}
        </span>
        <a
          href={entry.url}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className={`inline-flex items-center gap-1 text-sm font-medium whitespace-nowrap px-4 py-2 rounded-md transition-colors ${
            entry.status === '可用'
              ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
              : 'bg-secondary-300 text-foreground-500 cursor-not-allowed'
          }`}
          onClick={(e) => {
            if (entry.status !== '可用') e.preventDefault();
          }}
        >
          <i className="ri-external-link-line"></i>
          访问站点
        </a>
      </div>
    </div>
  );
}

function ArticleCard({ article }: { article: typeof latestArticles[0] }) {
  return (
    <Link
      to={`/article/${article.id}`}
      className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden block"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          title={`${article.title} - 禁漫天堂在线`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-5 flex flex-col gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-md text-xs whitespace-nowrap">
            {article.category}
          </span>
          <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
          <span className="text-xs text-foreground-400 whitespace-nowrap flex items-center gap-1">
            <i className="ri-time-line text-xs"></i>
            {article.readTime}
          </span>
        </div>
        <h3 className="text-base font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
      </div>
    </Link>
  );
}

function FaqItem({ faq, defaultOpen }: { faq: typeof faqList[0]; defaultOpen: boolean }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border border-background-200/70 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left bg-background-50 hover:bg-background-100 transition-colors"
      >
        <span className="text-sm font-medium text-foreground-950 pr-4">{faq.question}</span>
        <span className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-foreground-500"></i>
        </span>
      </button>
      {open && (
        <div className="px-5 pb-5">
          <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
        </div>
      )}
    </div>
  );
}

export default function Home() {
  const siteUrl = 'https://jmcomic.com';

  return (
    <>
      <SEOHead
        title="禁漫天堂在线 – 官方入口与最新访问指南"
        description="禁漫天堂在线为成人漫画爱好者提供海量正版内容，本页汇总最新可用入口、访问方法及常见问题解答，帮助用户快速稳定地访问禁漫天堂在线平台，畅享丰富漫画资源。"
        keywords="禁漫天堂在线,禁漫天堂最新入口,禁漫天堂访问方法,禁漫天堂官网地址,禁漫天堂镜像站,成人漫画在线阅读"
        canonicalUrl={siteUrl}
        ogImage="https://readdy.ai/api/search-image?query=Modern%20digital%20manga%20reading%20platform%20with%20warm%20amber%20lighting%2C%20clean%20minimalist%20design%2C%20japanese%20illustration%20style%2C%20elegant%20composition%2C%20soft%20gradients&width=1200&height=630&seq=home-og-01&orientation=landscape"
      />
      <SchemaOrg type="Organization" data={{
        name: '禁漫天堂在线',
        url: siteUrl,
        description: '禁漫天堂在线为成人漫画爱好者提供海量正版内容',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'support@jmcomic.com',
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
      }} />
      <SchemaOrg type="WebSite" data={{
        name: '禁漫天堂在线',
        url: siteUrl,
        description: '禁漫天堂在线为成人漫画爱好者提供海量正版内容',
      }} />
      <SchemaOrg type="WebPage" data={{
        name: '禁漫天堂在线 – 官方入口与最新访问指南',
        url: siteUrl,
        description: '禁漫天堂在线为成人漫画爱好者提供海量正版内容，本页汇总最新可用入口、访问方法及常见问题解答。',
        isPartOf: { '@type': 'WebSite', name: '禁漫天堂在线', url: siteUrl },
      }} />
      <SchemaOrg type="FAQPage" data={{
        mainEntity: faqList.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20artistic%20manga%20panel%20composition%20with%20flowing%20ink%20textures%2C%20warm%20amber%20and%20deep%20rose%20gradients%2C%20japanese%20aesthetic%20style%2C%20soft%20atmospheric%20lighting%2C%20minimalist%20geometric%20patterns%2C%20elegant%20dark%20background%20with%20golden%20accents&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
                alt="禁漫天堂在线"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55"></div>
            </div>
            <div className="relative z-10 w-full text-center px-4 md:px-6">
              <h1 className="text-3xl md:text-5xl font-heading text-background-50 mb-4">
                禁漫天堂在线
              </h1>
              <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto mb-8 leading-relaxed">
                最新官方入口汇总与访问指南 &mdash; 帮助您快速稳定地访问禁漫天堂在线平台，
                畅享海量正版成人漫画资源
              </p>
              <a
                href="#entries"
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-compass-3-line"></i>
                查看最新入口
              </a>
            </div>
          </section>

          <section id="entries" className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  最新可用入口
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  以下是禁漫天堂当前所有可用的访问地址，我们持续更新确保入口有效。
                  如遇某个入口无法访问，请尝试其他备用入口。
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {siteEntries.map((entry) => (
                  <EntryCard key={entry.id} entry={entry} />
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  热门漫画推荐
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  精选平台热门漫画作品，涵盖多种题材风格，总有一部适合您
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {mangaList.slice(0, 8).map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/category?c=${manga.category}`}
                    className="group block"
                  >
                    <div className="aspect-[2/3] overflow-hidden rounded-lg bg-background-200 mb-3">
                      <img
                        src={manga.cover}
                        alt={manga.title}
                        title={`${manga.title} - 禁漫天堂在线`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-sm font-medium text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {manga.title}
                    </h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{manga.author}</span>
                      <span className="text-xs text-accent-500 whitespace-nowrap flex items-center gap-0.5">
                        <i className="ri-star-fill text-[10px]"></i>
                        {manga.rating}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="text-center mt-8">
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 bg-secondary-100 text-secondary-700 px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-secondary-200 transition-colors whitespace-nowrap"
                >
                  浏览全部漫画
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  最新访问指南
                </h2>
                <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                  获取禁漫天堂最新访问教程、使用技巧及常见问题解决方案
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </section>

          <section id="faq" className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  常见问题解答
                </h2>
                <p className="text-sm text-foreground-500">
                  关于禁漫天堂在线平台的常见疑问，在这里都能找到答案
                </p>
              </div>
              <div className="flex flex-col gap-3">
                {faqList.map((faq) => (
                  <FaqItem key={faq.id} faq={faq} defaultOpen={faq.id === 1} />
                ))}
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}